/**
 * SizeToggle — the Expand / Minimise button on the review and Fix windows.
 *
 * "pane" is the view laid over the task pane; Expand opens it as a window
 * over Word. "window" is that Office window; Minimise puts it back over the
 * pane. Word cannot resize an open dialog, so the switch closes one and opens
 * the other; the pane holds the state, so nothing is lost.
 */

import { makeStyles } from "@fluentui/react-components";
import type React from "react";
import { fonts, inkColors, surfaceColors } from "../tokens";

export type ViewPlacement = "pane" | "window";

const useStyles = makeStyles({
  btn: {
    height: "28px",
    padding: "0 10px",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    borderRadius: "6px",
    border: `1px solid ${surfaceColors.lineStrong}`,
    backgroundColor: surfaceColors.paper,
    color: inkColors.ink2,
    fontFamily: fonts.body,
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    ":hover": { backgroundColor: surfaceColors.paperDim },
  },
  glyph: { fontSize: "13px", lineHeight: 1 },
});

export const SizeToggle: React.FC<{ placement: ViewPlacement; onToggle?: () => void }> = ({ placement, onToggle }) => {
  const styles = useStyles();
  if (!onToggle) return null;
  const expand = placement === "pane";
  return (
    <button
      type="button"
      className={styles.btn}
      onClick={onToggle}
      title={expand ? "Open over the whole Word window" : "Shrink back over the side panel"}
    >
      <span className={styles.glyph} aria-hidden="true">
        {expand ? "⤢" : "⤡"}
      </span>
      {expand ? "Expand" : "Minimise"}
    </button>
  );
};

export default SizeToggle;
